import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { DataProps } from '../types/utils'

export const useDataProps = <T extends { id: number }, U>(
  props: DataProps<T, U>,
  fetchDataFromApi: (id: number) => Promise<T>
) => {
  const { id, data, refetch: refetchData, ...rest } = props
  const [fetchedData, setFetchedData] = useState<T | undefined>()
  const { isPending, refetch } = useQuery({
    queryKey: ['dataProps', id],
    queryFn: async () => {
      // If data is provided, return it directly
      if (data) {
        setFetchedData(data)
        return { data, ...rest }
      }
      // If id is provided, fetch data from the API
      if (id) {
        const fetchedData = await fetchDataFromApi(id)
        setFetchedData(fetchedData)
        return { data: fetchedData, ...rest }
      }
      throw new Error('Either id or data must be provided')
    },
    enabled: !!id || !!data
  })

  useEffect(() => {
    if (data) setFetchedData(data)
  }, [data])

  return {
    data: fetchedData,
    id: id ?? data.id,
    isPending,
    refetch: data
      ? refetchData
      : async () => {
          await refetch()
          await refetchData?.()
        },
    ...rest
  }
}
